import { assertArray, assertBoolean, assertInteger, assertNonEmptyString, assertNumber, assertOneOf, assertOptionalString, assertRecord, assertString } from '@fleece/utilities';
import {
  BACKTEST_EXIT_REASONS,
  BACKTEST_STATUSES,
  BacktestPosition,
  BacktestResult,
  BacktestStats,
  BacktestTemplate,
  CreditSpreadBacktest,
  CreditSpreadChainRow,
  DailyBar,
  EARNINGS_COVERAGES,
  EARNINGS_TIMINGS,
  GetCreditSpreadChainResponse,
  ListEarningsResponse,
  OPTION_RIGHTS,
  StartCreditSpreadBacktestRequest,
} from './credit-spread-api';

/**
 * Reading the credit-spread responses back, for the same reason `wire.ts` exists: a
 * response is checked field by field rather than cast. These are plain numbers, so the
 * checking is all this does — but a chain row missing its strike should fail here, by
 * name, rather than render as `NaN` in a table.
 */

function reviveEach<T>(value: unknown, field: string, revive: (entry: unknown, entryField: string) => T): ReadonlyArray<T> {
  return assertArray(value, field).map((entry, index) => revive(entry, `${field}[${index}]`));
}

function optionalNumber(value: unknown, field: string): number | undefined {
  return value === undefined || value === null ? undefined : assertNumber(value, field);
}

export function reviveCreditSpreadChainRow(value: unknown, field = 'row'): CreditSpreadChainRow {
  const r = assertRecord(value, field);
  return {
    symbol: assertNonEmptyString(r['symbol'], `${field}.symbol`),
    expiration: assertNonEmptyString(r['expiration'], `${field}.expiration`),
    dte: assertInteger(r['dte'], `${field}.dte`),
    strike: assertNumber(r['strike'], `${field}.strike`),
    moneyness: assertNumber(r['moneyness'], `${field}.moneyness`),
    bid: optionalNumber(r['bid'], `${field}.bid`),
    ask: optionalNumber(r['ask'], `${field}.ask`),
    mid: optionalNumber(r['mid'], `${field}.mid`),
    spreadPct: optionalNumber(r['spreadPct'], `${field}.spreadPct`),
    last: optionalNumber(r['last'], `${field}.last`),
    lastAt: optionalNumber(r['lastAt'], `${field}.lastAt`),
    delta: optionalNumber(r['delta'], `${field}.delta`),
    iv: optionalNumber(r['iv'], `${field}.iv`),
    neighborStrike: optionalNumber(r['neighborStrike'], `${field}.neighborStrike`),
    neighborPriceDiff: optionalNumber(r['neighborPriceDiff'], `${field}.neighborPriceDiff`),
    neighborRelativeDiff: optionalNumber(r['neighborRelativeDiff'], `${field}.neighborRelativeDiff`),
  };
}

export function reviveCreditSpreadChain(value: unknown, field = 'response'): GetCreditSpreadChainResponse {
  const r = assertRecord(value, field);
  const underlying = assertRecord(r['underlying'], `${field}.underlying`);
  return {
    underlying: {
      symbol: assertNonEmptyString(underlying['symbol'], `${field}.underlying.symbol`),
      price: assertNumber(underlying['price'], `${field}.underlying.price`),
      pricedAt: assertNumber(underlying['pricedAt'], `${field}.underlying.pricedAt`),
    },
    right: assertOneOf(r['right'], `${field}.right`, OPTION_RIGHTS),
    expirations: reviveEach(r['expirations'], `${field}.expirations`, (entry, entryField) => assertNonEmptyString(entry, entryField)),
    rows: reviveEach(r['rows'], `${field}.rows`, reviveCreditSpreadChainRow),
  };
}

export function reviveDailyBar(value: unknown, field = 'bar'): DailyBar {
  const r = assertRecord(value, field);
  return {
    date: assertNonEmptyString(r['date'], `${field}.date`),
    open: assertNumber(r['open'], `${field}.open`),
    high: assertNumber(r['high'], `${field}.high`),
    low: assertNumber(r['low'], `${field}.low`),
    close: assertNumber(r['close'], `${field}.close`),
    volume: assertNumber(r['volume'], `${field}.volume`),
  };
}

export function reviveDailyBars(value: unknown, field = 'bars'): ReadonlyArray<DailyBar> {
  return reviveEach(value, field, reviveDailyBar);
}

function reviveBacktestRequest(value: unknown, field: string): StartCreditSpreadBacktestRequest {
  const r = assertRecord(value, field);
  return {
    shortSymbol: assertNonEmptyString(r['shortSymbol'], `${field}.shortSymbol`),
    longSymbol: assertNonEmptyString(r['longSymbol'], `${field}.longSymbol`),
    takeProfit: assertNumber(r['takeProfit'], `${field}.takeProfit`),
    stopLoss: assertNumber(r['stopLoss'], `${field}.stopLoss`),
    dteMin: assertInteger(r['dteMin'], `${field}.dteMin`),
    dteMax: assertInteger(r['dteMax'], `${field}.dteMax`),
    moneynessMin: assertNumber(r['moneynessMin'], `${field}.moneynessMin`),
    moneynessMax: assertNumber(r['moneynessMax'], `${field}.moneynessMax`),
    entryTime: assertNonEmptyString(r['entryTime'], `${field}.entryTime`),
    shortDayEntryTime: assertNonEmptyString(r['shortDayEntryTime'], `${field}.shortDayEntryTime`),
    from: assertNonEmptyString(r['from'], `${field}.from`),
    to: assertNonEmptyString(r['to'], `${field}.to`),
    avoidEarnings: assertBoolean(r['avoidEarnings'], `${field}.avoidEarnings`),
  };
}

export function reviveEarnings(value: unknown, field = 'response'): ListEarningsResponse {
  const r = assertRecord(value, field);
  return {
    symbol: assertNonEmptyString(r['symbol'], `${field}.symbol`),
    coverage: assertOneOf(r['coverage'], `${field}.coverage`, EARNINGS_COVERAGES),
    completeFrom: assertNonEmptyString(r['completeFrom'], `${field}.completeFrom`),
    completeThrough: assertNonEmptyString(r['completeThrough'], `${field}.completeThrough`),
    reports: reviveEach(r['reports'], `${field}.reports`, (entry, entryField) => {
      const report = assertRecord(entry, entryField);
      return {
        date: assertNonEmptyString(report['date'], `${entryField}.date`),
        timing: assertOneOf(report['timing'], `${entryField}.timing`, EARNINGS_TIMINGS),
        fiscalPeriod: assertString(report['fiscalPeriod'], `${entryField}.fiscalPeriod`),
        reactionDate: assertNonEmptyString(report['reactionDate'], `${entryField}.reactionDate`),
      };
    }),
  };
}

function reviveTemplate(value: unknown, field: string): BacktestTemplate {
  const r = assertRecord(value, field);
  return {
    underlying: assertNonEmptyString(r['underlying'], `${field}.underlying`),
    right: assertOneOf(r['right'], `${field}.right`, OPTION_RIGHTS),
    expiration: assertNonEmptyString(r['expiration'], `${field}.expiration`),
    dte: assertInteger(r['dte'], `${field}.dte`),
    shortStrike: assertNumber(r['shortStrike'], `${field}.shortStrike`),
    longStrike: assertNumber(r['longStrike'], `${field}.longStrike`),
    underlyingPrice: assertNumber(r['underlyingPrice'], `${field}.underlyingPrice`),
    shortMoneyness: assertNumber(r['shortMoneyness'], `${field}.shortMoneyness`),
    widthPct: assertNumber(r['widthPct'], `${field}.widthPct`),
    shortSpreadPct: assertNumber(r['shortSpreadPct'], `${field}.shortSpreadPct`),
    longSpreadPct: assertNumber(r['longSpreadPct'], `${field}.longSpreadPct`),
    naturalCredit: assertNumber(r['naturalCredit'], `${field}.naturalCredit`),
  };
}

export function reviveBacktestPosition(value: unknown, field = 'position'): BacktestPosition {
  const r = assertRecord(value, field);
  return {
    entryDate: assertNonEmptyString(r['entryDate'], `${field}.entryDate`),
    entryAt: assertNumber(r['entryAt'], `${field}.entryAt`),
    expiration: assertNonEmptyString(r['expiration'], `${field}.expiration`),
    dte: assertInteger(r['dte'], `${field}.dte`),
    shortSymbol: assertNonEmptyString(r['shortSymbol'], `${field}.shortSymbol`),
    longSymbol: assertNonEmptyString(r['longSymbol'], `${field}.longSymbol`),
    shortStrike: assertNumber(r['shortStrike'], `${field}.shortStrike`),
    longStrike: assertNumber(r['longStrike'], `${field}.longStrike`),
    underlyingPrice: assertNumber(r['underlyingPrice'], `${field}.underlyingPrice`),
    shortMoneyness: assertNumber(r['shortMoneyness'], `${field}.shortMoneyness`),
    credit: assertNumber(r['credit'], `${field}.credit`),
    maxLoss: assertNumber(r['maxLoss'], `${field}.maxLoss`),
    exitReason: assertOneOf(r['exitReason'], `${field}.exitReason`, BACKTEST_EXIT_REASONS),
    exitAt: optionalNumber(r['exitAt'], `${field}.exitAt`),
    exitDate: assertOptionalString(r['exitDate'], `${field}.exitDate`),
    exitDebit: optionalNumber(r['exitDebit'], `${field}.exitDebit`),
    profit: optionalNumber(r['profit'], `${field}.profit`),
    daysHeld: optionalNumber(r['daysHeld'], `${field}.daysHeld`),
    returnOnCapital: optionalNumber(r['returnOnCapital'], `${field}.returnOnCapital`),
    annualizedReturn: optionalNumber(r['annualizedReturn'], `${field}.annualizedReturn`),
  };
}

function reviveStats(value: unknown, field: string): BacktestStats {
  const r = assertRecord(value, field);
  return {
    positions: assertInteger(r['positions'], `${field}.positions`),
    closed: assertInteger(r['closed'], `${field}.closed`),
    takeProfits: assertInteger(r['takeProfits'], `${field}.takeProfits`),
    stopLosses: assertInteger(r['stopLosses'], `${field}.stopLosses`),
    expired: assertInteger(r['expired'], `${field}.expired`),
    open: assertInteger(r['open'], `${field}.open`),
    winRate: optionalNumber(r['winRate'], `${field}.winRate`),
    averageDaysHeld: optionalNumber(r['averageDaysHeld'], `${field}.averageDaysHeld`),
    medianDaysHeld: optionalNumber(r['medianDaysHeld'], `${field}.medianDaysHeld`),
    averageProfit: optionalNumber(r['averageProfit'], `${field}.averageProfit`),
    totalProfit: assertNumber(r['totalProfit'], `${field}.totalProfit`),
    averageReturnOnCredit: optionalNumber(r['averageReturnOnCredit'], `${field}.averageReturnOnCredit`),
    averageReturnOnCapital: optionalNumber(r['averageReturnOnCapital'], `${field}.averageReturnOnCapital`),
    annualizedReturnOnCapital: optionalNumber(r['annualizedReturnOnCapital'], `${field}.annualizedReturnOnCapital`),
  };
}

function reviveResult(value: unknown, field: string): BacktestResult {
  const r = assertRecord(value, field);
  const skips = assertRecord(r['skips'], `${field}.skips`);
  return {
    template: reviveTemplate(r['template'], `${field}.template`),
    positions: reviveEach(r['positions'], `${field}.positions`, reviveBacktestPosition),
    skips: {
      noCandidates: assertInteger(skips['noCandidates'], `${field}.skips.noCandidates`),
      noPrints: assertInteger(skips['noPrints'], `${field}.skips.noPrints`),
      noCredit: assertInteger(skips['noCredit'], `${field}.skips.noCredit`),
      earnings: assertInteger(skips['earnings'], `${field}.skips.earnings`),
      earningsUnknown: assertInteger(skips['earningsUnknown'], `${field}.skips.earningsUnknown`),
      creditAboveWidth: assertInteger(skips['creditAboveWidth'], `${field}.skips.creditAboveWidth`),
    },
    stats: reviveStats(r['stats'], `${field}.stats`),
  };
}

export function reviveCreditSpreadBacktest(value: unknown, field = 'backtest'): CreditSpreadBacktest {
  const r = assertRecord(value, field);
  const progress = assertRecord(r['progress'], `${field}.progress`);
  return {
    backtestId: assertNonEmptyString(r['backtestId'], `${field}.backtestId`),
    status: assertOneOf(r['status'], `${field}.status`, BACKTEST_STATUSES),
    startedAt: assertNumber(r['startedAt'], `${field}.startedAt`),
    finishedAt: optionalNumber(r['finishedAt'], `${field}.finishedAt`),
    request: reviveBacktestRequest(r['request'], `${field}.request`),
    progress: {
      done: assertInteger(progress['done'], `${field}.progress.done`),
      total: assertInteger(progress['total'], `${field}.progress.total`),
      stage: assertString(progress['stage'], `${field}.progress.stage`),
    },
    error: assertOptionalString(r['error'], `${field}.error`),
    result: r['result'] === undefined ? undefined : reviveResult(r['result'], `${field}.result`),
  };
}
